import React from "react";
import { GamePhase, PlayerRole } from "../../context/GameContext";

interface GameInfoProps {
  phase: GamePhase;
  dayCount: number;
  role: PlayerRole;
  isAlive: boolean;
  killResult: string | null;
  detectiveResult: { targetId: string; isMafia: boolean } | null;
}

const GameInfo: React.FC<GameInfoProps> = ({
  phase,
  dayCount,
  role,
  isAlive,
  killResult,
  detectiveResult,
}) => {
  const getRoleColor = () => {
    switch (role) {
      case PlayerRole.MAFIA:
        return "text-red-500";
      case PlayerRole.DOCTOR:
        return "text-green-500";
      case PlayerRole.DETECTIVE:
        return "text-blue-500";
      case PlayerRole.VILLAGER:
        return "text-yellow-500";
      default:
        return "text-gray-300";
    }
  };

  const getPhaseName = () => {
    switch (phase) {
      case GamePhase.DAY:
        return "Day Phase";
      case GamePhase.VOTING:
        return "Voting Phase";
      case GamePhase.NIGHT:
        return "Night Phase";
      default:
        return "Waiting";
    }
  };

  const getPhaseDescription = () => {
    if (!isAlive) {
      return "You are dead. You can observe but cannot participate.";
    }

    switch (phase) {
      case GamePhase.DAY:
        return "Discuss with other players and try to identify the Mafia.";
      case GamePhase.VOTING:
        return "Vote for who you think is a Mafia member, or skip voting.";
      case GamePhase.NIGHT:
        switch (role) {
          case PlayerRole.MAFIA:
            return "Choose a player to eliminate.";
          case PlayerRole.DOCTOR:
            return "Choose a player to save from the Mafia.";
          case PlayerRole.DETECTIVE:
            return "Choose a player to investigate.";
          default:
            return "Sleep while the special roles act.";
        }
      default:
        return "Waiting for the game to start.";
    }
  };

  return (
    <div className="card-transparent">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-bold">{getPhaseName()}</h2>
          <p className="text-sm text-slate-400">Day {dayCount}</p>
        </div>
        <div
          className={`px-3 py-1 rounded ${
            phase === GamePhase.NIGHT
              ? "bg-indigo-900"
              : phase === GamePhase.VOTING
              ? "bg-purple-800"
              : "bg-amber-700"
          }`}
        >
          {phase === GamePhase.NIGHT
            ? "🌙 Night"
            : phase === GamePhase.VOTING
            ? "🗳️ Voting"
            : "☀️ Day"}
        </div>
      </div>

      <div className="mb-4 pb-4 border-b border-slate-700">
        <p className="text-sm text-slate-300 mb-2">Your Role:</p>
        <div className={`text-xl font-bold ${getRoleColor()}`}>
          {role.charAt(0).toUpperCase() + role.slice(1)}
          {!isAlive && " (Dead)"}
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-sm font-semibold text-slate-300 mb-2">
          Current Phase:
        </h3>
        <p className="text-sm">{getPhaseDescription()}</p>
      </div>

      {phase === GamePhase.DAY && killResult && (
        <div className="mt-4 p-3 bg-red-900/30 border border-red-800 rounded-md fade-in">
          <p className="text-sm font-medium">
            <span className="text-red-400">🩸 </span>A player was killed during
            the night!
          </p>
        </div>
      )}

      {phase === GamePhase.DAY && killResult === null && dayCount > 1 && (
        <div className="mt-4 p-3 bg-green-900/30 border border-green-800 rounded-md fade-in">
          <p className="text-sm font-medium">
            <span className="text-green-400">✅ </span>
            Nobody was killed during the night.
          </p>
        </div>
      )}

      {phase === GamePhase.DAY &&
        role === PlayerRole.DETECTIVE &&
        detectiveResult && (
          <div className="mt-4 p-3 bg-blue-900/30 border border-blue-800 rounded-md fade-in">
            <p className="text-sm font-medium">
              <span className="text-blue-400">🔍 </span>
              Your investigation reveals that the player is
              {detectiveResult.isMafia ? (
                <span className="text-red-500 font-bold"> a mafia member</span>
              ) : (
                <span className="text-green-500 font-bold">
                  {" "}
                  not a mafia member
                </span>
              )}
            </p>
          </div>
        )}
    </div>
  );
};

export default GameInfo;
